import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export const HIGHLIGHT_PULSE_KEYFRAMES: Keyframe[] = [
  { transform: 'scale(1)', boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.05)' },
  {
    transform: 'scale(1.015)',
    boxShadow: '0 18px 36px -10px rgba(0, 0, 0, 0.35)',
  },
  { transform: 'scale(1)', boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.05)' },
];

export const HIGHLIGHT_PULSE_OPTIONS: KeyframeAnimationOptions = {
  duration: 1000,
  iterations: 3,
  easing: 'ease-out',
};

export const highlightElement = (
  el: HTMLElement | null,
  {
    block = 'center' as ScrollLogicalPosition,
    offsetTop,
    keyframes = HIGHLIGHT_PULSE_KEYFRAMES,
    options = HIGHLIGHT_PULSE_OPTIONS,
  }: {
    block?: ScrollLogicalPosition;
    offsetTop?: number;
    keyframes?: Keyframe[];
    options?: KeyframeAnimationOptions;
  } = {}
) => {
  if (!el) return;
  if (offsetTop != null) el.style.scrollMarginTop = `${offsetTop}px`;
  el.scrollIntoView({ behavior: 'smooth', block });
  el.animate(keyframes, options);
};

export const useHighlightSection = ({
  matchValue,
  getElement,
  enabled = true,
  paramName = 'highlight',
}: {
  matchValue: string;
  getElement: () => HTMLElement | null;
  enabled?: boolean;
  paramName?: string;
}): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const intentRef = useRef(false);

  useEffect(() => {
    if (searchParams.get(paramName) === matchValue) {
      intentRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.delete(paramName);
      setSearchParams(next, { replace: true });
    }

    if (!intentRef.current || !enabled) return;
    const el = getElement();
    if (!el) return;

    intentRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.animate(HIGHLIGHT_PULSE_KEYFRAMES, HIGHLIGHT_PULSE_OPTIONS);
  }, [searchParams, enabled]);
};
